import { FC, useEffect } from "react";
import { Modal, Form, Input, Select, Button, Popconfirm, message } from "antd";
import { Settings, Archive, Trash2 } from "lucide-react";
import { useUpdateProject, useDeleteProject } from "~/services/project";
import { useMissionControlStore } from "../mission-control.store";

type Props = {
  visible: boolean;
  onClose: () => void;
};

export const ProjectSettingsModal: FC<Props> = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const { selectedProject, setSelectedProject } = useMissionControlStore();

  const updateMutation = useUpdateProject();
  const deleteMutation = useDeleteProject();

  useEffect(() => {
    if (selectedProject && visible) {
      form.setFieldsValue({
        name: selectedProject.name,
        description: selectedProject.description || "",
        status: selectedProject.status || "ACTIVE",
      });
    }
  }, [selectedProject, visible, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedProject) return;

      await updateMutation.mutateAsync({
        id: selectedProject.id,
        data: values,
      });

      message.success("Project updated successfully");

      // Update the selected project in the store
      setSelectedProject({ ...selectedProject, ...values });

      onClose();
    } catch (error: any) {
      if (error.errorFields) {
        // Validation error
        return;
      }
      console.error("Error updating project:", error);
      message.error("Failed to update project");
    }
  };

  const handleArchive = async () => {
    if (!selectedProject) return;

    try {
      const isCurrentlyActive = selectedProject.status === "ACTIVE";
      const newStatus = isCurrentlyActive ? "INACTIVE" : "ACTIVE";

      await updateMutation.mutateAsync({
        id: selectedProject.id,
        data: {
          name: selectedProject.name,
          description: selectedProject.description,
          status: newStatus,
        },
      });

      message.success(
        isCurrentlyActive
          ? "Project archived successfully"
          : "Project restored successfully"
      );

      // Update the selected project in the store
      setSelectedProject({ ...selectedProject, status: newStatus });

      onClose();
    } catch (error) {
      console.error("Error archiving project:", error);
      message.error("Failed to archive project");
    }
  };

  const handleDelete = async () => {
    if (!selectedProject) return;

    try {
      await deleteMutation.mutateAsync(selectedProject.id);

      message.success("Project deleted successfully");

      // Clear the selected project
      setSelectedProject(null);

      onClose();
    } catch (error) {
      console.error("Error deleting project:", error);
      message.error("Failed to delete project");
    }
  };

  if (!selectedProject) return null;

  const isArchived = selectedProject.status === "INACTIVE";

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <Settings size={20} className="text-blue-600" />
          <span>Project Settings</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <div className="space-y-6 pt-4">
        {/* Project Form */}
        <Form form={form} layout="vertical">
          <Form.Item
            label="Project Name"
            name="name"
            rules={[{ required: true, message: "Please enter project name" }]}
          >
            <Input placeholder="Enter project name" size="large" />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea
              placeholder="Enter project description"
              rows={4}
            />
          </Form.Item>

          <Form.Item label="Status" name="status">
            <Select size="large">
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="INACTIVE">Archived</Select.Option>
            </Select>
          </Form.Item>
        </Form>

        {/* Action Buttons */}
        <div className="space-y-3 pt-4 border-t">
          <div className="flex items-center justify-between">
            <Button
              type="primary"
              size="large"
              onClick={handleSave}
              loading={updateMutation.isPending}
              className="flex-1 mr-2"
            >
              Save Changes
            </Button>

            <Popconfirm
              title={
                isArchived
                  ? "Restore this project?"
                  : "Archive this project?"
              }
              description={
                isArchived
                  ? "This project will be restored and visible in the booking dropdown."
                  : "This project will be hidden from the booking dropdown but data will be preserved."
              }
              onConfirm={handleArchive}
              okText="Yes"
              cancelText="No"
            >
              <Button
                icon={<Archive size={16} />}
                size="large"
                loading={updateMutation.isPending}
              >
                {isArchived ? "Restore" : "Archive"}
              </Button>
            </Popconfirm>
          </div>

          <Popconfirm
            title="Delete this project?"
            description={
              <div className="max-w-sm">
                <p className="mb-2">
                  This will permanently delete the project and all associated data.
                </p>
                <p className="font-semibold text-red-600">
                  This action cannot be undone!
                </p>
              </div>
            }
            onConfirm={handleDelete}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<Trash2 size={16} />}
              size="large"
              loading={deleteMutation.isPending}
              className="w-full"
            >
              Delete Project
            </Button>
          </Popconfirm>
        </div>
      </div>
    </Modal>
  );
};
